import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { Command } from '../../../menu/types';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import {
  BANK_STATEMENT_IMPORT_STATUS,
  type BankStatementPreview,
  type BankStatementPreviewRow,
  useImportBankStatementMutation,
  useParseBankStatementPreviewMutation,
} from '../../api/subscriptionProofBankImportApi';

type SubscriptionProofBankImportDialogProps = {
  command: Command;
  nodeKey: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

const ACCEPTED_FILE_TYPES =
  '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ACCEPTED_EXTENSIONS = ['csv', 'xlsx'];

const formatDate = (value: string | null) => {
  if (!value) return '—';

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === BANK_STATEMENT_IMPORT_STATUS.matched) return 'success';
  if (status === BANK_STATEMENT_IMPORT_STATUS.needReview) return 'warning';
  if (status === BANK_STATEMENT_IMPORT_STATUS.duplicate) return 'error';
  return 'default';
};

const getImportMessage = (result: { message: string; skippedCount: number; errors: string[] }) => {
  const details = [
    result.skippedCount > 0 ? `Skipped: ${result.skippedCount}.` : null,
    result.errors.length > 0 ? result.errors.join(' ') : null,
  ].filter(Boolean);

  return [result.message, ...details].filter(Boolean).join(' ');
};

export const SubscriptionProofBankImportDialog = ({
  command,
  nodeKey,
  onClose,
  onSuccess,
}: SubscriptionProofBankImportDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<BankStatementPreview | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [parsePreview, parseState] = useParseBankStatementPreviewMutation();
  const [importStatement, importState] = useImportBankStatementMutation();

  const selectedCount = useMemo(
    () => preview?.rows.filter((row) => row.selected).length ?? 0,
    [preview]
  );
  const previewCounts = useMemo(
    () => ({
      matched:
        preview?.rows.filter((row) => row.status === BANK_STATEMENT_IMPORT_STATUS.matched).length ??
        0,
      needReview:
        preview?.rows.filter((row) => row.status === BANK_STATEMENT_IMPORT_STATUS.needReview)
          .length ?? 0,
      duplicate:
        preview?.rows.filter((row) => row.status === BANK_STATEMENT_IMPORT_STATUS.duplicate)
          .length ?? 0,
    }),
    [preview]
  );

  const error = validationError
    ? validationError
    : parseState.error
      ? extractErrorMessage(parseState.error, 'Failed to parse bank statement.')
      : importState.error
        ? extractErrorMessage(importState.error, 'Failed to import bank statement.')
        : null;

  const updateRows = (updater: (rows: BankStatementPreviewRow[]) => BankStatementPreviewRow[]) => {
    setPreview((current) => (current ? { ...current, rows: updater(current.rows) } : current));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ACCEPTED_EXTENSIONS.includes(extension)) {
      setValidationError('Select a CSV or XLSX bank statement file.');
      return;
    }

    setValidationError(null);
    setSelectedFileName(file.name);
    setPreview(null);

    try {
      setPreview(await parsePreview({ file }).unwrap());
    } catch {
      // The request error is rendered from RTK Query state.
    }
  };

  const handleSubscriptionChange = (rowId: string, value: string) => {
    const subscriptionId = Number(value);
    const subscription = preview?.subscriptions.find(
      (item) => item.infSubscriptionId === subscriptionId
    );

    updateRows((rows) =>
      rows.map((row) =>
        row.rowId !== rowId
          ? row
          : {
              ...row,
              infSubscriptionId: subscriptionId || null,
              subscriptionName: subscription?.infSubscriptionName ?? '',
              status:
                row.status === BANK_STATEMENT_IMPORT_STATUS.needReview && subscriptionId
                  ? BANK_STATEMENT_IMPORT_STATUS.matched
                  : row.status,
            }
      )
    );
  };

  const handleImport = async () => {
    const rows = preview?.rows.filter((row) => row.selected) ?? [];
    if (rows.length === 0) {
      setValidationError('Select at least one row to import.');
      return;
    }

    setValidationError(null);
    try {
      const result = await importStatement({ nodeKey, rows }).unwrap();
      onSuccess(getImportMessage(result));
    } catch {
      // The request error is rendered from RTK Query state.
    }
  };

  const isBusy = parseState.isLoading || importState.isLoading;

  return (
    <Dialog open onClose={isBusy ? undefined : onClose} fullWidth maxWidth="xl">
      <DialogTitle>{command.caption || 'Import bank statement — preview'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!preview && (
            <>
              <Typography variant="body2" color="text.secondary">
                Select a CSV or XLSX bank statement to review its transactions before import.
              </Typography>
              <Box>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  hidden
                  onChange={handleFileChange}
                />
                <Button
                  variant="contained"
                  startIcon={
                    parseState.isLoading ? <CircularProgress size={18} /> : <UploadFileIcon />
                  }
                  disabled={parseState.isLoading}
                  onClick={() => inputRef.current?.click()}
                >
                  {parseState.isLoading ? 'Parsing…' : 'Choose file'}
                </Button>
                {selectedFileName && (
                  <Typography component="span" variant="body2" sx={{ ml: 2 }}>
                    {selectedFileName}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {preview && (
            <>
              <Box>
                <Typography variant="body2">
                  <strong>File:</strong> {preview.fileName || selectedFileName || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Period:</strong> {formatDate(preview.periodFrom)} —{' '}
                  {formatDate(preview.periodTo)}
                </Typography>
                <Typography variant="body2">
                  <strong>Transactions:</strong> {preview.rows.length} | <strong>Matched:</strong>{' '}
                  {previewCounts.matched} | <strong>Need review:</strong> {previewCounts.needReview}{' '}
                  | <strong>Duplicate:</strong> {previewCounts.duplicate}
                </Typography>
              </Box>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={600} variant="body2">
                  Selected: {selectedCount} / {preview.rows.length}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isBusy || selectedCount === 0}
                  onClick={() =>
                    updateRows((rows) => rows.map((row) => ({ ...row, selected: false })))
                  }
                >
                  Clear selection
                </Button>
              </Stack>

              <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: 'divider' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Status</TableCell>
                      <TableCell>Payment date</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>Subscription name</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((row) => (
                      <TableRow key={row.rowId} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={row.selected}
                            disabled={isBusy}
                            onChange={() =>
                              updateRows((rows) =>
                                rows.map((item) =>
                                  item.rowId === row.rowId
                                    ? { ...item, selected: !item.selected }
                                    : item
                                )
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color={getStatusColor(row.status)}
                          />
                        </TableCell>
                        <TableCell>{formatDate(row.paymentDate)}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" disabled={isBusy}>
                            <InputLabel id={`subscription-${row.rowId}`}>Subscription</InputLabel>
                            <Select
                              labelId={`subscription-${row.rowId}`}
                              label="Subscription"
                              value={row.infSubscriptionId?.toString() ?? ''}
                              onChange={(event) =>
                                handleSubscriptionChange(row.rowId, event.target.value)
                              }
                            >
                              {preview.subscriptions.map((subscription) => (
                                <MenuItem
                                  key={subscription.infSubscriptionId}
                                  value={subscription.infSubscriptionId}
                                >
                                  {subscription.infSubscriptionName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>{row.currency}</TableCell>
                        <TableCell align="right">{row.amount.toFixed(2)}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            aria-label="Remove transaction"
                            size="small"
                            disabled={isBusy}
                            onClick={() =>
                              updateRows((rows) => rows.filter((item) => item.rowId !== row.rowId))
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>
        {preview && (
          <Button
            variant="contained"
            disabled={isBusy || selectedCount === 0}
            onClick={handleImport}
          >
            {importState.isLoading ? <CircularProgress size={20} /> : 'Import selected'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionProofBankImportDialog;
